import * as types from '../mutation-types'
import {Api} from '@/api/api'
import {DateTime} from 'luxon'
import {debounce} from 'debounce'
import {getCheckoutData} from '@/utils/bookingData'
import {getJsDate, interval, intervalsOverlap} from '@/utils/date'
import {isEmpty, times} from '@/utils/general'
import {cloneDeep} from 'lodash'
import {router} from '@/router'

const debouncedGetAvailabilities = debounce(function(dispatch) {
  /* istanbul ignore next */
  dispatch('campground/getAvailabilities', null, { root: true })
}, 200)

const debouncedCheckPrice = debounce(function(dispatch) {
  /* istanbul ignore next */
  dispatch('campground/checkPrice', null, { root: true })
}, 200)

const getBillingFromBookingDetails = confirmation => {
  return {
    city: confirmation.city,
    country: { code: confirmation.country },
    lastname: confirmation.last_name,
    mail: confirmation.email,
    message: confirmation.message,
    name: confirmation.first_name,
    phone: confirmation.phone,
    street: confirmation.street1,
    street_additional: confirmation.street2,
    zip: confirmation.zip_code,
  }
}

// initial state
export const initialState = () => {
  return {
    aid: null,
    billing: {},
    car: {},
    checkout: {
      data: {},
      responseAdyen: {},
      responseCheckout: {},
      status: {
        error: false,
        loading: false,
        success: false,
      },
    },
    confirmation: {},
    dates: [],
    displayedDate: null,
    isCompleted: false,
    price: {
      subtotal: 0,
      total: 0,
      currency_alt: { total: 0 },
    },
    selectedCampsite: null,
    selectedExtras: [],
    selectedPersons: [],
    token: null,
    travellers: {},
    oldBookingId: null,
    onlinePayment: false,
    processingPayment: false,
    campsiteData: {},
    draftbookingToken: null,
    draftBookingId: null,
    }
  }
let state = initialState()

// getters
const getters = {
  aid: state => state.aid,
  billing: state =>
    state.isCompleted ? getBillingFromBookingDetails(state.confirmation) : state.billing,
  car: state =>
    state.isCompleted
      ? {
          length: state.confirmation.vehicle_length,
          license_plate: state.confirmation.license_plate,
          width: state.confirmation.vehicle_width,
        }
      : state.car,
  checkout: state => state.checkout,
  confirmation: state => state.confirmation,
  dates: state =>
    state.isCompleted
      ? [state.confirmation.booking_data.date_from, state.confirmation.booking_data.date_until]
      : state.dates,
  displayedDate: state => getJsDate(state.displayedDate),
  isCompleted: state => state.isCompleted,
  price: state => {
    if (state.isCompleted) {
      return state.confirmation.pricelist
    }
    return state.price
  },
  selectedCampsite: state => state.selectedCampsite,
  selectedExtras: state => state.selectedExtras,
  selectedPersons: state => state.selectedPersons,
  status: state => state.status,
  token: state => state.token,
  travellers: state => (state.isCompleted ? state.confirmation.guests : state.travellers),
  processingPayment: state => state.processingPayment,
  campsiteData: state => state.campsiteData,
  draftbookingToken: state => state.draftbookingToken,
  draftBookingId: state => state.draftBookingId,
}

// actions
const actions = {
  handleStoredCheckout({ commit, state, rootGetters }) {
    /* reset adyen response */
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'responseAdyen',
      value: {},
    })

    const checkoutData = getCheckoutData({
      aid: state.aid,
      billing: state.billing,
      car: state.car,
      dates: state.dates,
      selectedCampsite: state.selectedCampsite,
      selectedExtras: state.selectedExtras,
      selectedPersons: state.selectedPersons,
      travellers: state.travellers,
    })

    checkoutData.oldBookingId = state.oldBookingId
    checkoutData.payment_system = 'ON_SITE'
    const paymentInfos = rootGetters['campground/paymentInfos']
    if (paymentInfos && paymentInfos.rentals && paymentInfos.pitches) {
      checkoutData.payment_system = state.selectedCampsite.campsite_type === 'accommodation'
        ? paymentInfos.rentals.system
        : paymentInfos.pitches.system
    }
    if (checkoutData.aid === null) checkoutData.aid = 'e4f1afde-65cd-432f-b38e-288af3919e93'

    const oldCheckoutData = state.checkout.data
    // TODO: brutal hack to test
    // oldCheckoutData.oldBookingId = checkoutData.oldBookingId
    /* check if there are any checkoutData or checkoutData have changed */
    const bookingId = state.checkout.responseCheckout.booking_id
    if (
      !isEmpty(oldCheckoutData) &&
      JSON.stringify(checkoutData) !== JSON.stringify(oldCheckoutData) &&
      bookingId
    ) {
      /* delete pending booking - result of call doesn't matter to FE */
      const cancelBookingUrl = rootGetters['campground/urls'].cancel_pending_booking
      Api.getData(`${cancelBookingUrl}?booking_id=${bookingId}`)

      /* reset checkoutData & responseCheckout */
      commit(types.BOOKING_SET_CHECKOUT, {
        key: 'data',
        value: {},
      })
      commit(types.BOOKING_SET_CHECKOUT, {
        key: 'responseCheckout',
        value: {},
      })
    }
  },
  getBookingDetails({ rootGetters, dispatch }, bookingId) {
    const bookingDetailsUrl = rootGetters['campground/urls'].booking_details
    const campgroundSlug = rootGetters['campground/campground'].slug
    return Api.getData(`${bookingDetailsUrl}${campgroundSlug}/${bookingId}/`)
      .then(result => {
        /* set confirmation data and completed state */
        dispatch('setBookingDetails', result)
        return result
      })
      .catch(e => {
        throw e
      })
  },
  setBookingDetails({ commit, dispatch }, data) {
    commit(types.BOOKING_SET_CONFIRMATION, data)
    commit(types.BOOKING_SET_COMPLETED, true)

    if (data.aid) {
      commit(types.BOOKING_SET_AID, data.aid)
    }

    /* set currency based confirmation data */
    dispatch('application/setCurrency', { code: data.pricelist.currency }, { root: true })
  },
  setAid({ commit }, string) {
    commit(types.BOOKING_SET_AID, string)
  },
  setGid({ commit }, string) {
    commit(types.BOOKING_SET_GID, string)
  },
  setProcessingPayment({commit}, boolean){
    commit(types.BOOKING_SET_PROCESSING_PAYMENT, boolean)
  },
  setBilling({ commit }, { k, v }) {
    commit(types.BOOKING_SET_BILLING, { k, v })
  },
  setCar({ commit }, { k, v }) {
    commit(types.BOOKING_SET_CAR, { k, v })
  },
  setCompleted({ commit }, value) {
    commit(types.BOOKING_SET_COMPLETED, value)
  },
  setRequestedTravelData({ rootGetters, dispatch }, { acco, adults, children }) {
    let selectedCampsite = null
    let selectedAdult = null
    let selectedChild = null

    // if children are requested, we want to skip automatic selection of guests,
    // because it's unclear how the campsite defines children and adults.
    // if (children > 0) {
    //   children = 0
    //   adults = 0
    // }

    if (acco) {
      const availableCampsites = rootGetters['campground/campground'].campsites
      selectedCampsite = availableCampsites.filter(c => c.id === acco)
      if (selectedCampsite.length) {
        selectedCampsite = cloneDeep(selectedCampsite[0])
        selectedCampsite.amount = 1
      } else {
        selectedCampsite = null
      }
    }


    let availableChildren = cloneDeep(
      rootGetters['campground/campground'].persons.filter(person => person.person_type === 'child')
    )
    let availableAdults = cloneDeep(
      rootGetters['campground/campground'].persons.filter(person => person.person_type === 'adult')
    )

    // If there are two or more adults defined, we do not want to set selectedAdult
    if (availableAdults.length) {
      if (adults) {
        selectedAdult = availableAdults[0]
      }
    }

    // If there are two or more adults defined, we do not want to set selectedChild
    if (availableChildren.length) {
      if (children) {
        selectedChild = availableChildren[0]
      }
    }

    let step1fullfilled = false

    if (children && selectedChild) {
      dispatch('setPerson', {
        amount: children,
        person: selectedChild,
      })
    }
    if (adults && selectedAdult) {
      dispatch('setPerson', {
        amount: adults,
        person: selectedAdult,
      })
      dispatch(
        'application/setStepStatus',
        { number: 1, key: 'fulfilled', value: true },
        { root: true }
      )
      step1fullfilled = !children || (children && selectedChild)
    }
    if (selectedCampsite) {
      dispatch(
        'application/setStepStatus',
        { number: 2, key: 'selectedCampsiteType', value: selectedCampsite.campsite_type },
        { root: true }
      )
      dispatch('setSelectedCampsite', selectedCampsite)
      if (step1fullfilled) {
        let params = {}
        if (router.currentRoute && router.currentRoute.params) {
          params = router.currentRoute.params
        }
        setTimeout(() => {
          router.push({
            name: 'Step',
            params: { ...params, step: 2 },
          })
        }, 10)
      }
    }
  },
  setDates({ commit, dispatch, state, rootGetters }, dates) {
    commit(types.BOOKING_SET_DATES, dates)
    /* istanbul ignore else */
    if (dates && dates.length === 2 && dates[1] !== null && dates[0] !== null) {
      if (state.selectedPersons.length > 0) {
        dispatch('campground/getAvailabilities', null, { root: true })
      }
    }
    dispatch('campground/checkPrice', null, { root: true })
  },
  setDisplayedDate({ commit }, displayedDate) {
    commit(types.BOOKING_SET_DISPLAYED_DATE, displayedDate)
  },
  setSelectedCampsite({ commit, dispatch, state, rootGetters }, campsite) {
    const extras = rootGetters['campground/campground'].extras
    commit(types.BOOKING_SET_SELECTED_CAMPSITE, { campsite, extras })
    if (state.selectedCampsite !== null) {
      dispatch(
        'application/setStepStatus',
        {
          number: 2,
          key: 'fulfilled',
          value: true,
        },
        { root: true }
      )
    } else {
      dispatch(
        'application/setStepStatus',
        {
          number: 2,
          key: 'fulfilled',
          value: false,
        },
        { root: true }
      )
    }
    dispatch('campground/checkPrice', null, { root: true })
  },
  setPerson({ commit, dispatch, state, rootGetters }, { amount, person }) {
    const campgroundPersons = rootGetters['campground/campground'].persons
    commit(types.BOOKING_SET_PERSON, { amount, person, campgroundPersons })

    if (state.selectedPersons.length > 0) {
      debouncedGetAvailabilities(dispatch)
    }
    debouncedCheckPrice(dispatch)
  },
  setExtra({ commit, dispatch }, { extra, amount }) {
    commit(types.BOOKING_SET_EXTRA, { extra, amount })
    debouncedCheckPrice(dispatch)
  },
  setCheckout({ commit }, { key, value }) {
    commit(types.BOOKING_SET_CHECKOUT, { key, value })
  },
  setTraveller({ commit }, { k, i, v }) {
    commit(types.BOOKING_SET_TRAVELLER, { k, i, v })
  },
  setDraftbookingToken({ commit }, token) {
    commit(types.BOOKING_SET_DRAFTBOOKING_TOKEN, token)
  },
  setDraftBookingId({ commit }, id) {
    try {
    } catch (e) {}
    commit(types.BOOKING_SET_DRAFT_BOOKING_ID, id)
  },
  setToken({ commit }, string) {
    commit(types.BOOKING_SET_TOKEN, string)
  },
  setPrice({ commit }, price) {
    commit(types.BOOKING_SET_PRICE, price)
  },
  setCampsiteData({ commit }, campsiteData) {
    commit(types.CAMPSITE_SET_DATA, campsiteData)
  },
  resetAdyen({ commit }) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'responseAdyen',
      value: {},
    })
  },
  resetCheckout({ commit }) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'responseCheckout',
      value: {},
    })
  },
  resetState({ commit }) {
    const initState = initialState()
    commit(types.BOOKING_RESET_STATE, initState)
  },
  resetStatus({ commit }) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'status',
      value: { loading: false, error: false, success: false },
    })
  },
  performCheckout({ commit, dispatch, state, rootGetters }, options = {}) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'status',
      value: { loading: true, error: false, success: false },
    })

    // Determine if we have a draftbooking token (prefer store, fallback to URL)
    let draftToken = state.draftbookingToken || null
    if (!draftToken) {
      try {
        const q = router && router.currentRoute && router.currentRoute.query
        if (q && q.draftbooking) {
          draftToken = q.draftbooking
        }
      } catch (e) {
        // ignore
      }
    }

    // Build payload
    let bookingData
    const hasBasicSelection = !!(state.selectedCampsite && state.dates && state.dates.length === 2)

    if (draftToken && !hasBasicSelection) {
      // Minimal payload (backend returns draft data); token will be added as query, not payload
      bookingData = {}
      if (state.aid) bookingData.aid = state.aid
    } else {
      bookingData = getCheckoutData({
        aid: state.aid,
        billing: state.billing,
        car: state.car,
        dates: state.dates,
        selectedCampsite: state.selectedCampsite,
        selectedExtras: state.selectedExtras,
        selectedPersons: state.selectedPersons,
        travellers: state.travellers,
      })

      bookingData.oldBookingId = state.oldBookingId
      bookingData.onlinePayment = state.onlinePayment

      // Only set payment_system if a campsite is selected
      if (state.selectedCampsite) {
        const paymentInfos = rootGetters['campground/paymentInfos']
        bookingData.payment_system = 'ON_SITE'
        if (paymentInfos && paymentInfos.rentals && paymentInfos.pitches) {
          bookingData.payment_system = state.selectedCampsite.campsite_type === 'accommodation'
            ? paymentInfos.rentals.system
            : paymentInfos.pitches.system
        }
      }
      bookingData.old_booking_id = state.oldBookingId
      bookingData.gid = state.gid
      // Add price list into payload only when saving a draft booking
      if (options && options.draftBooking === true) {
        try {
          const price = state.price || {}
          const hasPrice = (price && (price.total || (price.components && price.components.length)))
          if (hasPrice) {
            // Send a copy of the price list without request_data (not needed in payload)
            const priceCopy = { ...price }
            if (Object.prototype.hasOwnProperty.call(priceCopy, 'request_data')) {
              delete priceCopy.request_data
            }
            bookingData.draft_booking_pricelist = priceCopy
          }
        } catch (e) {
          // ignore price serialization errors
        }
      }

      // Do not add draft flags/tokens to payload anymore; they will be sent as query params
    }

    if (bookingData.aid === null) bookingData.aid = 'e4f1afde-65cd-432f-b38e-288af3919e93'

    // Build checkout URL with query parameters
    let checkoutUrl = rootGetters['campground/urls'].checkout
    const query = []
    if (options && options.draftBooking === true) {
      // Only when saving a draft booking, pass the token as draft_booking_id
      query.push('draft_booking=true')
      if (draftToken) {
        query.push(`draft_booking_id=${encodeURIComponent(draftToken)}`)
      }
    }
    if (query.length) {
      checkoutUrl += (checkoutUrl.indexOf('?') === -1 ? '?' : '&') + query.join('&')
    }

    return Api.postData(checkoutUrl, bookingData)
      .then(result => {
        // add created date to result only for real checkout (not for draft save)
        if (!(options && options.draftBooking === true)) {
          result.created = new Date()
        }

        // If backend returned draft booking data, apply it to our store state
        if (result && result.draft_booking) {
          dispatch('applyDraftBooking', result.draft_booking)
        }

        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'data',
          value: bookingData,
        })
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'responseCheckout',
          value: result,
        })
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'status',
          value: { loading: false, error: false, success: true },
        })
        dispatch(
          'application/setStepStatus',
          { number: 2, key: 'selectedCampsiteIsAvailable', value: true },
          { root: true }
        )
        commit(types.BOOKING_SET_OLD_BOOKING_ID, result.booking_id)

        // If we used a draft token, remove it from the URL to avoid repeated calls
        if (draftToken) {
          try {
            const curr = router.currentRoute
            const newQuery = { ...(curr.query || {}) }
            delete newQuery.draftbooking
            router.replace({ name: curr.name, params: curr.params, query: newQuery })
          } catch (e) {
            // ignore URL cleanup errors
          }
        }
        return result
      })
      .catch(e => {
        if (!e.response) e.response = 'unknown error'
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'status',
          value: { loading: false, error: true, success: false },
        })
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'responseCheckout',
          value: e.response,
        })
        throw e
      })
  },
  /**
   * Apply draft booking data returned from API into the current store state
   * This prefills the forms so the user can continue the booking.
   */
  applyDraftBooking({ dispatch, rootGetters }, draft) {
    if (!draft || typeof draft !== 'object') return

    // Dates
    if (draft.date_from && draft.date_until) {
      dispatch('setDates', [draft.date_from, draft.date_until])
    }

    // Selected campsite by id (with amount if present)
    let campsiteWasSet = false
    const campsites = draft.campsites || draft.accommodation_types
    if (campsites && Array.isArray(campsites) && campsites.length > 0) {
      const campsiteData = campsites[0]
      const availableCampsites = rootGetters['campground/campground']
        ? rootGetters['campground/campground'].campsites
        : []
      const found = (availableCampsites || []).find(c => c.id === campsiteData.id)
      if (found) {
        const campsite = cloneDeep(found)
        campsite.amount = campsiteData.amount || 1
        dispatch('setSelectedCampsite', campsite)
        campsiteWasSet = true
        // Also reflect campsite type in stepper
        try {
          const type = campsite.campsite_type
          dispatch('application/setStepStatus', { number: 2, key: 'selectedCampsiteType', value: type }, { root: true })
        } catch (e) { /* noop */ }
      }

      // Persons on the campsite object
      const persons = campsiteData.persons || campsiteData.accommodation_type_persons
      if (Array.isArray(persons)) {
        persons.forEach(p => {
          if (p && p.id !== undefined && p.amount !== undefined) {
            dispatch('setPerson', { amount: p.amount, person: { id: p.id } })
          }
        })
      }
    }

    // Extras
    if (Array.isArray(draft.extras)) {
      draft.extras.forEach(ex => {
        if (ex && ex.id !== undefined) {
          dispatch('setExtra', { extra: { id: ex.id }, amount: ex.amount || 1 })
        }
      })
    }

    // Billing
    const billingMap = {
      city: 'city',
      country: 'country',
      email: 'mail',
      first_name: 'name',
      last_name: 'lastname',
      message: 'message',
      phone: 'phone',
      street1: 'street',
      street2: 'street_additional',
      zip_code: 'zip',
    }
    let hasName = false
    let hasLast = false
    let hasMail = false
    Object.keys(billingMap).forEach(apiKey => {
      const storeKey = billingMap[apiKey]
      if (draft[apiKey] !== undefined && draft[apiKey] !== null) {
        if (apiKey === 'country') {
          try {
            const countries = rootGetters['campground/countries'] || []
            const code = String(draft[apiKey]).toLowerCase()
            const countryObj = countries.find(c => String(c.code).toLowerCase() === code)
            dispatch('setBilling', { k: storeKey, v: countryObj || { code: draft[apiKey] } })
          } catch (e) {
            dispatch('setBilling', { k: storeKey, v: { code: draft[apiKey] } })
          }
        } else {
          dispatch('setBilling', { k: storeKey, v: draft[apiKey] })
          // Prefill confirm email when email comes from draft
          if (apiKey === 'email') {
            dispatch('setBilling', { k: 'mail_repeat', v: draft[apiKey] })
          }
        }
        if (apiKey === 'first_name') hasName = true
        if (apiKey === 'last_name') hasLast = true
        if (apiKey === 'email') hasMail = true
      }
    })

    // Persist draftbooking token from URL for later requests
    try {
      const q = router && router.currentRoute && router.currentRoute.query
      if (q && q.draftbooking) {
        dispatch('setDraftbookingToken', q.draftbooking)
      }
      if (q && q.draft_booking_id) {
        dispatch('setDraftBookingId', q.draft_booking_id)
      }
    } catch (e) { /* noop */ }

    // Persist draft_booking_id from draft payload if provided by backend
    try {
      if (draft && (draft.draft_booking_id || draft.id)) {
        dispatch('setDraftBookingId', draft.draft_booking_id || draft.id)
      }
    } catch (e) { /* noop */ }

    // Car
    if (draft.license_plate) dispatch('setCar', { k: 'license_plate', v: draft.license_plate })
    if (draft.vehicle_length) dispatch('setCar', { k: 'length', v: draft.vehicle_length })
    if (draft.vehicle_width) dispatch('setCar', { k: 'width', v: draft.vehicle_width })

    // Travellers/Guests
    if (Array.isArray(draft.guests)) {
      draft.guests.forEach((g, i) => {
        if (g.first_name) dispatch('setTraveller', { k: 'first_name', i, v: g.first_name })
        if (g.last_name) dispatch('setTraveller', { k: 'last_name', i, v: g.last_name })
        if (g.birthdate) dispatch('setTraveller', { k: 'birthdate', i, v: g.birthdate })
      })
    }

    // Update stepper statuses based on what we just applied
    try {
      const haveDates = !!(draft.date_from && draft.date_until)
      // Persons: count from draft (campsite.persons) if present
      let personsCount = 0
      const stepperCampsites = draft.campsites || draft.accommodation_types
      if (stepperCampsites && Array.isArray(stepperCampsites) && stepperCampsites[0]) {
        const stepperPersons = stepperCampsites[0].persons || stepperCampsites[0].accommodation_type_persons
        if (Array.isArray(stepperPersons)) {
          stepperPersons.forEach(p => {
            if (p && typeof p.amount === 'number') personsCount += p.amount
          })
        }
      }
      const personsOk = personsCount > 0

      if (haveDates && personsOk) {
        dispatch('application/setStepStatus', { number: 1, key: 'fulfilled', value: true }, { root: true })
      }

      if (campsiteWasSet) {
        dispatch('application/setStepStatus', { number: 2, key: 'fulfilled', value: true }, { root: true })
      }

      // Do not auto-skip Part 1: keep formPart1Passed=false so user starts with names
    } catch (e) {
      // ignore stepper update errors
    }
  },
  prepareBookingForPayment({ rootGetters }, { bookingId }) {
    const url = `${rootGetters['campground/urls'].prepare_booking}?booking_id=${bookingId}`
    return Api.getData(url).then(result => {
      return result
    })
  },
  processPayload({ rootGetters }, { id, payload }) {
    const data = {
      payload: payload,
      payment: id,
      testmode: rootGetters['campground/campground'].testmode,
    }

    return Api.postData(rootGetters['campground/urls'].payload, data)
      .then(result => {
        return result
      })
      .catch(e => {
        throw e
      })
  },
  resetTimer({ commit }) {
    commit(types.BOOKING_RESET_TIMER)
  },
}

// mutations
const mutations = {
  [types.BOOKING_RESET_STATE](state, initState) {
    Object.keys(initState).forEach(key => {
      // Always keep existing Affiliate ID!
      if (key !== 'aid' && key !== 'gid') {
        state[key] = initState[key]
      }
    })
  },
  [types.BOOKING_SET_AID](state, payload) {
    state.aid = payload
  },
  [types.BOOKING_SET_GID](state, payload) {
    state.gid = payload
  },
  [types.BOOKING_SET_BILLING](state, { k, v }) {
    state.billing = { ...state.billing, [k]: v }
  },
  [types.BOOKING_SET_CAR](state, { k, v }) {
    state.car = { ...state.car, [k]: v }
  },
  [types.BOOKING_SET_CHECKOUT](state, { key, value }) {
    state.checkout = { ...state.checkout, [key]: value }
  },
  [types.BOOKING_SET_CONFIRMATION](state, result) {
    state.confirmation = result
  },
  [types.BOOKING_SET_DATES](state, payload) {
    state.dates = payload
  },
  [types.BOOKING_SET_DISPLAYED_DATE](state, payload) {
    state.displayedDate = payload
  },
  [types.BOOKING_SET_EXTRA](state, { extra, amount }) {
    const old = state.selectedExtras.find(p => p.id === extra.id)

    // add value
    if (!old) state.selectedExtras = [...state.selectedExtras, { id: extra.id, amount: +amount }]
    // remove value
    if (!amount || amount === 0) {
      state.selectedExtras = state.selectedExtras.filter(e => e.id !== extra.id)
    } else {
      // update value
      state.selectedExtras = state.selectedExtras.map(ex => {
        if (ex.id === extra.id) return Object.assign({}, ex, { id: extra.id, amount: +amount })
        return ex
      })
    }
  },
  [types.BOOKING_SET_COMPLETED](state, value) {
    state.isCompleted = value
  },
  [types.BOOKING_SET_PERSON](state, { amount, person, campgroundPersons }) {
    const old = state.selectedPersons.find(p => p.id === person.id)

    // add value
    if (!old) state.selectedPersons = [...state.selectedPersons, { id: person.id, amount: +amount }]
    // remove value
    if (!amount || amount === 0) {
      state.selectedPersons = state.selectedPersons.filter(p => p.id !== person.id)
    } else {
      // update value
      state.selectedPersons = state.selectedPersons.map(p => {
        if (p.id === person.id) return Object.assign({}, p, { id: person.id, amount: +amount })
        return p
      })
    }

    // for every person and an traveller object to the travellers array
    state.travellers = []
    state.selectedPersons.forEach(person => {
      const p = campgroundPersons.find(p => p.id === person.id)
      /* istanbul ignore else */
      if (p && p.person_type !== 'pet') {
        times(person.amount)(() => {
          state.travellers.push({})
        })
      }
    })
  },
  [types.BOOKING_SET_SELECTED_CAMPSITE](state, { campsite, extras }) {
    state.selectedCampsite = campsite === state.selectedCampsite ? null : campsite

    // filter selected extras if they are valid for new selected campsite type
    /* istanbul ignore else */
    if (state.selectedCampsite !== null) {
      state.selectedExtras = state.selectedExtras.filter(extra => {
        const basicExtra = extras.find(ex => ex.id === extra.id)
        return basicExtra[`applies_for_${state.selectedCampsite.campsite_type}`]
      })
    }
  },
  [types.BOOKING_SET_SELECTED_SEASONS](state, payload) {
    state.selectedSeasons = payload
  },
  [types.BOOKING_SET_PRICE](state, payload) {
    state.price = payload
  },
  [types.BOOKING_SET_TRAVELLER](state, { k, i, v }) {
    const traveller = { ...state.travellers[i], [k]: v }
    state.travellers = { ...state.travellers, [i]: traveller }
  },
  [types.BOOKING_SET_TOKEN](state, payload) {
    state.token = payload
    Api.setToken(payload)
  },
  [types.BOOKING_RESET_TIMER](state) {
    state.checkout.responseCheckout = state.checkout.responseCheckout || {}
    state.checkout.responseCheckout.created = new Date()
  },
  [types.BOOKING_SET_OLD_BOOKING_ID](state, bookingId) {
    state.oldBookingId = bookingId
  },
  [types.BOOKING_SET_ONLINE_PAYMENT](state, payload){
    state.onlinePayment = payload
  },
    [types.BOOKING_SET_PROCESSING_PAYMENT](state, payload){
    state.processingPayment = payload
  },
  [types.CAMPSITE_SET_DATA](state, payload) {
    state.campsiteData = payload
  },
  [types.BOOKING_SET_DRAFTBOOKING_TOKEN](state, token) {
    state.draftbookingToken = token
  },
  [types.BOOKING_SET_DRAFT_BOOKING_ID](state, id) {
    state.draftBookingId = id
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
